/**
 * Why an add-to-cart was refused — as a VALUE, not an exception.
 *
 * The add path used to signal all nine business refusals by
 * `throw new Error("<English or French sentence>")`. Two delivery shells
 * consume it and they disagreed about what a throw means:
 *
 *   - `POST /api/cart` caught it and substring-matched the message
 *     (`includes("currently unavailable")` → `ITEM_UNAVAILABLE`, otherwise a
 *     bare 400) — so the buyer saw a sane sentence.
 *   - `addToCartAction` (a "use server" action) did not catch it at all. A
 *     throw crossing the server-action boundary is REDACTED by Next in
 *     production: the client receives "An error occurred in the Server
 *     Components render. The specific message is omitted…", which
 *     `useAddToCart` then set as `addError` and rendered.
 *
 * Incident 2026-08-09: a buyer hit the stock branch seven times in six
 * minutes on a listing that was reserved, and got that redacted digest text
 * every time instead of "this item is unavailable". They abandoned the cart
 * route and bought through quick-checkout on the third attempt.
 *
 * A refusal is an ORDINARY OUTCOME of asking to add an item, so it is part of
 * the return type. `throw` is left for genuine faults only (the RPC erroring),
 * where the redaction is correct behaviour.
 *
 * @see lib/cart-perform-add.ts — producer
 * @see app/api/cart/route.ts, app/actions/cart.ts — the two delivery shells
 * @see .tondocs/skills/type-driven-design.md — Make Illegal States Unrepresentable
 */

import type { AddToCartErrorCode } from "@kraahl/contract";
import { unreachable } from "@/lib/type-helpers";

export type AddToCartRejection =
  /** Neither a session nor a guest id — nothing to own the cart row. */
  | { kind: "no_identity" }
  /** Client claims a user id but the cookie session is anon (refresh race). */
  | { kind: "unauthenticated" }
  /** Chat-sourced add whose room/participant/message grant does not hold. */
  | { kind: "chat_grant_missing" }
  | { kind: "listing_missing"; listingId: string }
  /** Seller unlisted it. */
  | { kind: "listing_unlisted" }
  /** Dev listing, and this viewer is not allowed to see it. */
  | { kind: "listing_not_visible" }
  | { kind: "seller_banned" }
  | { kind: "listing_cancelled_by_seller" }
  /** Stock exhausted by other buyers' reservations, or sold. */
  | { kind: "out_of_stock" }
  | { kind: "invalid_quantity" };

export interface AddToCartRejectionDescription {
  code: AddToCartErrorCode;
  /** HTTP status for the route shell. */
  status: 400 | 403;
  /** Buyer-facing sentence. Rendered as-is by web and mobile. */
  message: string;
}

/**
 * Total function: rejection → what both delivery shells should say.
 *
 * Messages are preserved VERBATIM from the strings the throw sites used, so
 * this refactor changes the transport, not the copy. (Two of them are French
 * — pre-existing; i18n of buyer-facing cart copy is separate debt.)
 *
 * `listing_not_visible` deliberately shares its code AND its message with
 * `listing_unlisted`: telling a stranger "this is a dev listing you may not
 * see" leaks the listing's existence. The domain keeps the two apart, the
 * wire does not.
 */
/**
 * `add_to_cart_atomic` returns `kind: 'unavailable'` whenever
 * `available <= current` — which lumps together two situations that deserve
 * opposite answers. The payload it ships alongside tells them apart:
 *
 *   - `current > 0` → this buyer ALREADY holds every unit available to them.
 *     Nothing is wrong; their intent is satisfied. The common shape is a
 *     single-stock listing already sitting in their cart.
 *   - `current == 0` → nothing is available to anyone (sold, or held by other
 *     buyers' reservations). A real refusal.
 *
 * Collapsing both into "This item is currently unavailable (reserved or
 * sold)" is what a buyer saw seven times in six minutes on 2026-08-09 for an
 * item that was in their cart the whole time and that they went on to buy.
 */
export type UnavailableInterpretation =
  | { kind: "already_at_max"; quantity: number }
  | { kind: "out_of_stock" };

export function interpretUnavailable(payload: {
  available: number;
  current_quantity: number;
}): UnavailableInterpretation {
  return payload.current_quantity > 0
    ? { kind: "already_at_max", quantity: payload.current_quantity }
    : { kind: "out_of_stock" };
}

/**
 * Client-side carrier for a refusal.
 *
 * `CartProvider.addItem` is `Promise<void>`-with-throw and is called from a
 * dozen buttons; turning it into a result type is a separate refactor. So the
 * refusal keeps travelling as an exception ON THE CLIENT — but as a typed one
 * that carries the real sentence, instead of Next's redacted digest string.
 * Callers that only render `error.message` (`useAddToCart`) are fixed by
 * construction; callers that want to branch can `instanceof` and read
 * `rejection.kind`.
 */
export class AddToCartRejectedError extends Error {
  readonly rejection: AddToCartRejection;
  readonly code: AddToCartErrorCode;

  constructor(rejection: AddToCartRejection) {
    const { message, code } = describeAddToCartRejection(rejection);
    super(message);
    this.name = "AddToCartRejectedError";
    this.rejection = rejection;
    this.code = code;
  }
}

export function describeAddToCartRejection(
  rejection: AddToCartRejection,
): AddToCartRejectionDescription {
  switch (rejection.kind) {
    case "no_identity":
      return {
        code: "NO_CART_IDENTITY",
        status: 400,
        message: "Ni userId ni guestId fourni.",
      };
    case "unauthenticated":
      return { code: "UNAUTHENTICATED", status: 403, message: "Unauthorized" };
    case "chat_grant_missing":
      return { code: "CHAT_GRANT_MISSING", status: 403, message: "Forbidden" };
    case "listing_missing":
      return {
        code: "LISTING_NOT_FOUND",
        status: 400,
        message: `Listing introuvable ou supprimé (listingId=${rejection.listingId})`,
      };
    case "listing_unlisted":
    case "listing_not_visible":
      return {
        code: "LISTING_UNAVAILABLE",
        status: 400,
        message: "Sorry, this item isn't available anymore",
      };
    case "seller_banned":
      return {
        code: "SELLER_UNAVAILABLE",
        status: 400,
        message: "This item is no longer available from this seller.",
      };
    case "listing_cancelled_by_seller":
      return {
        code: "LISTING_CANCELLED",
        status: 400,
        message: "This item was cancelled by the seller.",
      };
    case "out_of_stock":
      return {
        code: "ITEM_UNAVAILABLE",
        status: 400,
        message: "This item is currently unavailable (reserved or sold).",
      };
    case "invalid_quantity":
      return {
        code: "INVALID_QUANTITY",
        status: 400,
        message: "Quantité invalide.",
      };
    default:
      return unreachable(rejection);
  }
}
